import { useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import { Alert, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { getFieldLabel } from "encamp-shared/src/waste/discrepancyFieldMeta";
import { gql } from "generated-graphql";
import { useState } from "react";
import { formatFieldValue } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog.helpers";
import { ManifestCorrectionValue } from "routes/Customer/Waste/Manifests/ManifestCorrectionValue";

import { CorrectionDraft, CorrectionFieldDraft, EMPTY_CORRECTION_DRAFT } from "./correctionDraft";
import { CorrectionFieldReview } from "./CorrectionFieldReview";
import { CorrectionManifestPicker } from "./CorrectionManifestPicker";
import { ExceedanceKey } from "./exceedanceKeys";

const OPEN_DETECTED_CORRECTION_QUERY = gql(`
  query GeneratorStatusExceedanceOpenDetectedCorrection(
    $facilityId: ID!, $processedManifestId: ID!, $wasteLineUnifiedId: ID, $field: String!
  ) {
    generatorStatusExceedanceOpenDetectedCorrection(
      facilityId: $facilityId, processedManifestId: $processedManifestId,
      wasteLineUnifiedId: $wasteLineUnifiedId, field: $field
    ) { id field eManifestValue vendorValue }
  }
`);

export function CreateManifestCorrectionFields({
  exceedanceKey,
  tenantId,
  draft,
  onDraftChange,
}: {
  exceedanceKey: ExceedanceKey;
  tenantId: string;
  draft: CorrectionDraft;
  onDraftChange: (draft: CorrectionDraft) => void;
}) {
  const [reviewOpen, setReviewOpen] = useState(false);
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <CorrectionManifestPicker
        exceedanceKey={exceedanceKey}
        manifest={draft.manifest ?? null}
        onChange={(manifest) => {
          onDraftChange({ ...EMPTY_CORRECTION_DRAFT, manifest });
          setReviewOpen(!!manifest);
        }}
      />
      {draft.fields.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            {draft.fields.map((item) => (
              <CorrectionSummary
                key={`${item.target.wasteLineUnifiedId}:${item.field}`}
                exceedanceKey={exceedanceKey}
                draft={item}
              />
            ))}
          </Stack>
          <Tooltip title="Edit corrections">
            <IconButton
              size="small"
              aria-label="Edit corrections"
              onClick={() => setReviewOpen(true)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      {draft.manifest && draft.fields.length === 0 && (
        <Button onClick={() => setReviewOpen(true)} sx={{ alignSelf: "flex-start" }}>
          Add corrections
        </Button>
      )}
      {reviewOpen && draft.manifest && (
        <CorrectionFieldReview
          exceedanceKey={exceedanceKey}
          tenantId={tenantId}
          manifest={draft.manifest}
          draft={draft}
          onClose={() => setReviewOpen(false)}
          onConfirm={(nextDraft) => {
            onDraftChange(nextDraft);
            setReviewOpen(false);
          }}
        />
      )}
    </Stack>
  );
}

function CorrectionSummary({
  exceedanceKey,
  draft,
}: {
  exceedanceKey: ExceedanceKey;
  draft: CorrectionFieldDraft;
}) {
  const { data } = useQuery(OPEN_DETECTED_CORRECTION_QUERY, {
    variables: {
      facilityId: exceedanceKey.facilityId,
      processedManifestId: draft.target.processedManifestId,
      wasteLineUnifiedId: draft.target.wasteLineUnifiedId,
      field: draft.field,
    },
  });
  const detected = data?.generatorStatusExceedanceOpenDetectedCorrection;
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">{draft.selectionLabel}</Typography>
      <ManifestCorrectionValue field={draft.field} value={draft.correctValue} />
      {detected && (
        <Alert severity="warning">
          The system already flagged {getFieldLabel(detected.field)} here: e-Manifest says{" "}
          {formatFieldValue(detected.field, detected.eManifestValue)}, vendor data says{" "}
          {formatFieldValue(detected.field, detected.vendorValue)}. You may want to link it from
          Link existing instead.
        </Alert>
      )}
    </Stack>
  );
}
